import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';

import {
    acquireAccessTokenWithForcedRefresh,
    getAccessToken,
    handleTokenAcquisitionFailure,
} from '@/auth/authUtils';
import { HTTP_STATUS_CODE_FORBIDDEN, HTTP_STATUS_CODE_UNAUTHORIZED } from '@/constants';
import useDialogStore from '@/stores/useDialogStore';
import { getEnv } from '@/utils/envUtils';

export const apiAxios = axios.create({
    baseURL: getEnv()?.VITE_API_BASE || '',
});

type RetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

export const retryUnauthorizedRequest = async (
    response: AxiosResponse<unknown>
): Promise<AxiosResponse<unknown>> => {
    const config = response.config as RetryableRequestConfig | undefined;

    if (!config || config._retry) {
        await handleTokenAcquisitionFailure();
        return response;
    }

    config._retry = true;

    const refreshedToken = await acquireAccessTokenWithForcedRefresh();
    if (!refreshedToken) {
        await handleTokenAcquisitionFailure();
        return response;
    }

    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${refreshedToken}`;

    let replayResponse: AxiosResponse<unknown>;
    try {
        replayResponse = await apiAxios.request(config);
    } catch {
        await handleTokenAcquisitionFailure();
        return response;
    }

    if (replayResponse.status === HTTP_STATUS_CODE_UNAUTHORIZED) {
        await handleTokenAcquisitionFailure();
    }

    return replayResponse;
};

export const initAxios = () => {
    apiAxios.defaults.validateStatus = (): boolean => true;
    apiAxios.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
        const token = await getAccessToken();

        if (token) {
            config.headers.Authorization = 'Bearer ' + token;
        }

        return config;
    });

    apiAxios.interceptors.response.use(
        async (response: AxiosResponse<unknown>): Promise<AxiosResponse<unknown>> => {
            if (response?.status === HTTP_STATUS_CODE_FORBIDDEN) {
                useDialogStore().showUnauthorizedAccessDialog = true;
                return response;
            }

            if (response?.status === HTTP_STATUS_CODE_UNAUTHORIZED) {
                return retryUnauthorizedRequest(response);
            }

            return response;
        }
    );
};
